"use client"

import { addMedia } from "@/(admin)/modules/media/services/apiServices"
import { EVENTS } from "@/constants/admin-paths"
import { t } from "@/languages/i18n"
import getDomains from "@/services/domain-services"
import { createEvent, getEventById, updateEvent } from "@/services/event-services"
import { getSubscribers } from "@/services/subscribers"
import { MediaData } from "@/types/types"
import { createPath } from "@/utils/create-path"
import { App, Form, UploadFile } from "antd"
import dayjs from "dayjs"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useState } from "react"

interface UseEventUpsertProps {
  onDataLoaded?: () => void;
}

export function useEventUpsert({ onDataLoaded }: UseEventUpsertProps = {}) {
  const [form] = Form.useForm()
  const router = useRouter()
  const searchParams = useSearchParams()
  const id = searchParams.get("id")
  const isEditMode = Boolean(id)

  const [websiteOptions, setWebsiteOptions] = useState<{ label: string; value: number }[]>([])
  const [subscriberOptions, setSubscriberOptions] = useState<{ label: string; value: number }[]>([])
  const [loadingWebsite, setLoadingWebsite] = useState(false)
  const [loadingSubscriber, setLoadingSubscriber] = useState(false)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [coverFileList, setCoverFileList] = useState<UploadFile[]>([])
  const [selectedCoverImage, setSelectedCoverImage] = useState<MediaData[]>([])

  const [status, setStatus] = useState("Draft")
  const [language, setLanguage] = useState("vi")
  const [publishDate, setPublishDate] = useState<string | null>(null)
  const { message } = App.useApp()

  const fetchWebsites = useCallback(async () => {
    try {
      setLoadingWebsite(true)
      const domains = await getDomains()
      setWebsiteOptions(
        domains.map(item => ({
          label: item.attributes?.domain ?? "",
          value: item.id,
        })),
      )
    } finally {
      setLoadingWebsite(false)
    }
  }, [])

  const fetchSubscribers = useCallback(async () => {
    try {
      setLoadingSubscriber(true)
      const res = await getSubscribers()
      setSubscriberOptions(
        res.data.map(item => ({
          label: item.attributes.email ?? item.attributes.name,
          value: item.id,
        })),
      )
    } finally {
      setLoadingSubscriber(false)
    }
  }, [])

  useEffect(() => {
    fetchWebsites()
    fetchSubscribers()
  }, [fetchWebsites, fetchSubscribers])

  useEffect(() => {
    if (!id) return

    const fetchEventDetail = async () => {
      try {
        setLoadingDetail(true)
        const res = await getEventById(Number(id))
        if (!res?.data) return

        const data = res.data.attributes
        const coverData = data.cover?.data

        setStatus(data.publishedAt ? "Published" : "Draft")
        setLanguage(data.locale || "vi")
        setPublishDate(data.publishedAt)

        form.setFieldsValue({
          name: data.name,
          location: data.location,
          contact: data.contact,
          shortDescription: data.shortDescription,
          detail: data.detail,
          locale: data.locale,
          date: data.date ? dayjs(data.date) : null,
          websites: data.websites?.data?.map((w: any) => w.id),
          subscribers: data.subscribers?.data?.map((s: any) => s.id),
          cover: coverData?.id,
        })

        if (coverData) {
          const mediaObj: MediaData = {
            id: coverData.id,
            attributes: coverData.attributes,
          } as unknown as MediaData
          setSelectedCoverImage([mediaObj])
          setCoverFileList([
            {
              uid: String(coverData.id),
              name: coverData.attributes.name,
              status: "done",
              url: coverData.attributes.url,
            },
          ])
        }
      } catch (error) {
        message.error(t("Tải sự kiện thất bại."))
      } finally {
        setLoadingDetail(false)
        if (onDataLoaded) onDataLoaded();
      }
    }

    fetchEventDetail()
  }, [id, form])

  const handleCoverUpload = async (options: any) => {
    const { file, onSuccess, onError } = options
    try {
      const formData = new FormData()
      formData.append("files", file)
      const res = await addMedia(formData)
      const media = res[0]

      form.setFieldValue("cover", media.id)
      setCoverFileList([{ uid: String(media.id), name: media.name, status: "done", url: media.url }])

      onSuccess(res)
      message.success(t("Tải lên ảnh bìa thành công!"))
    } catch (err) {
      message.error(t("Tải lên ảnh bìa thất bại"))
      onError(err)
    }
  }

  const onFinish = async (values: any): Promise<boolean> => {
    try {
      const payload = {
        name: values.name,
        location: values.location,
        contact: values.contact,
        shortDescription: values.shortDescription,
        detail: values.detail,
        locale: language,
        date: values.date ? values.date.toISOString() : null,
        cover: values.cover,
        websites: values.websites,
        subscribers: values.subscribers,
        publishedAt: status === "Draft" ? null : publishDate || new Date().toISOString(),
      }

      if (isEditMode) {
        await updateEvent(Number(id), payload)
        message.success(t("Cập nhật sự kiện thành công"))
      } else {
        await createEvent(payload)
        message.success(t("Tạo sự kiện mới thành công"))
        router.push(createPath(EVENTS.LIST.PATH, searchParams))
      }
      return true
    } catch {
      message.error(t("Lưu sự kiện thất bại"))
      return false
    }
  }

  return {
    form,
    isEditMode,
    websiteOptions,
    subscriberOptions,
    loadingWebsite,
    loadingSubscriber,
    loadingDetail,
    coverFileList,
    setCoverFileList,
    selectedCoverImage,
    setSelectedCoverImage,
    handleCoverUpload,
    onFinish,
    status,
    setStatus,
    language,
    setLanguage,
    publishDate,
    setPublishDate,
  }
}